import { html, svg, render, nothing, type TemplateResult } from "lit-html";
import { geoPath } from "d3-geo";
import { feature, mesh, merge } from "topojson-client";
import type { Topology, GeometryCollection, MultiPolygon, Polygon } from "topojson-specification";
import type { CountyStat } from "./types.js";
import { showTip, hideTip, tipRow } from "./tooltip.js";
import { money, signedPct, pct, number } from "./format.js";
import { icons } from "./icons.js";

export type MapMetric = "revenue" | "yoy" | "conversion" | "coverage";

type Topo = Topology<{ counties: GeometryCollection<{ name: string }>; states: GeometryCollection<{ name: string }>; nation: GeometryCollection }>;

interface Geo {
  topo: Topo;
  paths: { id: string; d: string; bbox: [number, number, number, number] }[];
  statesPath: string;
}

let geoPromise: Promise<Geo> | null = null;
export function loadGeo(): Promise<Geo> {
  if (!geoPromise) {
    geoPromise = fetch("/geo/counties.json", { credentials: "same-origin" })
      .then((r) => {
        if (!r.ok) throw new Error("Couldn't load the county map.");
        return r.json() as Promise<Topo>;
      })
      .then((topo) => {
        const gp = geoPath();
        const fc = feature(topo, topo.objects.counties);
        const paths = fc.features.map((f) => {
          const b = gp.bounds(f);
          return { id: String(f.id), d: gp(f) ?? "", bbox: [b[0][0], b[0][1], b[1][0], b[1][1]] as [number, number, number, number] };
        });
        const statesPath = gp(mesh(topo, topo.objects.states, (a, b) => a !== b)) ?? "";
        return { topo, paths, statesPath };
      })
      .catch((e) => { geoPromise = null; throw e; });
  }
  return geoPromise;
}

export interface MapData {
  counties: CountyStat[];
  metric: MapMetric;
  territoryRepIds: number[] | null; // outline the territory of these reps
  showLowConversion: boolean;
  selected: string | null;
  onSelect: (fips: string | null) => void;
  height?: number;
  fitKey: string; // when this changes, re-fit the view to the data
}

const W = 975, H = 610;
const YOY_STEPS = [-0.3, -0.1, -0.02, 0.02, 0.1, 0.3];
const CONV_STEPS = [0.15, 0.25, 0.35, 0.45, 0.55];

export function quantileSteps(values: number[], classes = 7): number[] {
  const v = values.filter((x) => x > 0).sort((a, b) => a - b);
  if (v.length === 0) return [];
  const steps: number[] = [];
  for (let i = 1; i < classes; i++) {
    const q = v[Math.min(v.length - 1, Math.floor((i / classes) * v.length))]!;
    if (!steps.length || q > steps[steps.length - 1]!) steps.push(q);
  }
  return steps;
}
const binOf = (v: number, steps: number[]) => { let i = 0; while (i < steps.length && v >= steps[i]!) i++; return i; };

export interface Legend { kind: "seq" | "div"; colors: string[]; labels: [string, string]; note?: string }

export function colorFor(metric: MapMetric, c: CountyStat | undefined, steps: number[], territory: Set<string> | null): { cls: string; legend?: never } {
  if (!c) return { cls: "" };
  switch (metric) {
    case "revenue":
      if (c.ytd <= 0) return { cls: "" };
      return { cls: `f-s${Math.min(7, binOf(c.ytd, steps) + 1)}` };
    case "yoy":
      if (c.yoy === null) return { cls: c.ytd > 0 ? "f-new" : "" };
      return { cls: `f-d${binOf(c.yoy, YOY_STEPS)}` };
    case "conversion":
      if (c.conversion === null || c.quotesResolved < 3) return { cls: "" };
      return { cls: `f-s${binOf(c.conversion, CONV_STEPS) + 2}` };
    case "coverage": {
      if (territory) return { cls: territory.has(c.fips) ? "f-s5" : "" };
      const n = c.reps.length;
      return { cls: n === 0 ? "" : `f-s${Math.min(7, 2 + (n - 1) * 2)}` };
    }
  }
}

export function legendFor(metric: MapMetric, steps: number[], hasTerritory: boolean): Legend {
  switch (metric) {
    case "revenue":
      return { kind: "seq", colors: [1, 2, 3, 4, 5, 6, 7].slice(0, steps.length + 1).map((i) => `var(--seq-${i})`), labels: [steps.length ? `< ${short(steps[0]!)}` : "$0", steps.length ? `${short(steps[steps.length - 1]!)}+` : ""], note: "Revenue YTD (each band ≈ same number of counties)" };
    case "yoy":
      return { kind: "div", colors: ["var(--div-neg-3)", "var(--div-neg-2)", "var(--div-neg-1)", "var(--div-mid)", "var(--div-pos-1)", "var(--div-pos-2)", "var(--div-pos-3)"], labels: ["−30% or worse", "+30% or better"], note: "YTD vs same period last year" };
    case "conversion":
      return { kind: "seq", colors: [2, 3, 4, 5, 6, 7].map((i) => `var(--seq-${i})`), labels: ["< 15%", "55%+"], note: "Quotes won, last 12 months (3+ quotes)" };
    case "coverage":
      return hasTerritory
        ? { kind: "seq", colors: ["var(--seq-5)"], labels: ["Active in last 2 years", ""], note: "Rep territory" }
        : { kind: "seq", colors: ["var(--seq-2)", "var(--seq-4)", "var(--seq-6)", "var(--seq-7)"], labels: ["1 rep", "4+ reps"], note: "Reps active per county, last 2 years" };
  }
}

const short = (n: number) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n)}`);

export class CountyMapEl extends HTMLElement {
  private _data: MapData | null = null;
  private geo: Geo | null = null;
  private view = { x: 0, y: 0, w: W, h: H };
  private lastFit = "";
  private svgEl: SVGSVGElement | null = null;
  private pointers = new Map<number, { x: number; y: number }>();
  private dragStart: { x: number; y: number; view: { x: number; y: number; w: number; h: number }; dist?: number } | null = null;
  private moved = false;
  private downFips: string | null = null;
  private error: string | null = null;

  set data(d: MapData) {
    this._data = d;
    if (!this.geo) {
      loadGeo().then((g) => { this.geo = g; this.draw(); }).catch((e: Error) => { this.error = e.message; this.draw(); });
    }
    this.draw();
  }

  disconnectedCallback() { hideTip(); }

  private fit(counties: CountyStat[]) {
    if (!this.geo) return;
    const ids = new Set(counties.filter((c) => c.ytd > 0 || c.priorYtd > 0 || c.quotesResolved > 0).map((c) => c.fips));
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of this.geo.paths) {
      if (!ids.has(p.id)) continue;
      x0 = Math.min(x0, p.bbox[0]); y0 = Math.min(y0, p.bbox[1]); x1 = Math.max(x1, p.bbox[2]); y1 = Math.max(y1, p.bbox[3]);
    }
    if (!Number.isFinite(x0)) { this.view = { x: 0, y: 0, w: W, h: H }; return; }
    const pad = Math.max(x1 - x0, y1 - y0) * 0.1 + 8;
    // extra room at the bottom so the legend doesn't sit on the data
    x0 -= pad; y0 -= pad; x1 += pad; y1 += pad + (y1 - y0) * 0.28;
    // keep the container's aspect ratio
    const rect = this.getBoundingClientRect();
    const aspect = rect.width > 0 && rect.height > 0 ? rect.width / rect.height : W / H;
    let w = x1 - x0, h = y1 - y0;
    if (w / h < aspect) { const nw = h * aspect; x0 -= (nw - w) / 2; w = nw; } else { const nh = w / aspect; y0 -= (nh - h) / 2; h = nh; }
    this.view = { x: x0, y: y0, w, h };
  }

  private applyView() {
    this.svgEl?.setAttribute("viewBox", `${this.view.x} ${this.view.y} ${this.view.w} ${this.view.h}`);
  }

  private zoom(factor: number, cx?: number, cy?: number) {
    const v = this.view;
    const nw = Math.min(W * 1.4, Math.max(25, v.w / factor));
    const nh = nw * (v.h / v.w);
    const px = cx ?? v.x + v.w / 2, py = cy ?? v.y + v.h / 2;
    this.view = { x: px - ((px - v.x) * nw) / v.w, y: py - ((py - v.y) * nh) / v.h, w: nw, h: nh };
    this.applyView();
  }

  private toMap(clientX: number, clientY: number) {
    const r = this.svgEl!.getBoundingClientRect();
    return { x: this.view.x + ((clientX - r.left) / r.width) * this.view.w, y: this.view.y + ((clientY - r.top) / r.height) * this.view.h };
  }

  private onPointerDown = (e: PointerEvent) => {
    if (this.pointers.size === 0) this.downFips = (e.target as Element).getAttribute?.("data-fips") ?? null;
    this.svgEl?.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.moved = false;
    const pts = [...this.pointers.values()];
    this.dragStart = { x: e.clientX, y: e.clientY, view: { ...this.view }, dist: pts.length === 2 ? Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) : undefined };
    this.svgEl?.classList.add("dragging");
  };
  private onPointerMove = (e: PointerEvent) => {
    if (!this.pointers.has(e.pointerId)) { this.hover(e); return; }
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const s = this.dragStart;
    if (!s || !this.svgEl) return;
    const r = this.svgEl.getBoundingClientRect();
    const pts = [...this.pointers.values()];
    if (pts.length === 2 && s.dist) {
      const d = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      const mid = this.toMap((pts[0]!.x + pts[1]!.x) / 2, (pts[0]!.y + pts[1]!.y) / 2);
      this.view = { ...s.view };
      this.zoom(d / s.dist, mid.x, mid.y);
      this.moved = true;
      return;
    }
    const dx = ((e.clientX - s.x) / r.width) * s.view.w, dy = ((e.clientY - s.y) / r.height) * s.view.h;
    if (Math.abs(e.clientX - s.x) + Math.abs(e.clientY - s.y) > 4) this.moved = true;
    this.view = { ...s.view, x: s.view.x - dx, y: s.view.y - dy };
    this.applyView();
    hideTip();
  };
  private onPointerUp = (e: PointerEvent) => {
    this.pointers.delete(e.pointerId);
    this.svgEl?.classList.remove("dragging");
    if (this.pointers.size === 0) {
      if (!this.moved && this._data) {
        const fips = this.downFips;
        this._data.onSelect(fips && fips !== this._data.selected ? fips : null);
      }
      this.dragStart = null;
    } else {
      const p = [...this.pointers.values()][0]!;
      this.dragStart = { x: p.x, y: p.y, view: { ...this.view } };
    }
  };
  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const p = this.toMap(e.clientX, e.clientY);
    this.zoom(Math.exp(-e.deltaY * 0.0022), p.x, p.y);
  };

  private hover(e: PointerEvent) {
    const fips = (e.target as Element).getAttribute?.("data-fips");
    if (!fips || !this._data) { hideTip(); return; }
    const c = this._data.counties.find((x) => x.fips === fips);
    const name = c?.label ?? (e.target as Element).getAttribute("data-name") ?? fips;
    if (!c) { showTip(html`<div class="tt-title">${name}</div><div class="muted">No sales or quotes in this view</div>`, e.clientX, e.clientY); return; }
    showTip(html`<div class="tt-title">${name}</div>
      ${tipRow("Revenue YTD", money(c.ytd))}
      ${tipRow("Same period last yr", money(c.priorYtd))}
      ${tipRow("Change", signedPct(c.yoy))}
      ${tipRow("Customers YTD", number(c.customers))}
      ${tipRow("Quotes won (12 mo)", c.quotesResolved ? `${pct(c.conversion)} · ${c.quotesWon}/${c.quotesResolved}` : "—")}
      ${tipRow("Top rep", c.topRepName || "—")}
      ${c.lowConversion ? html`<div class="tt-row" style="margin-top:4px;color:var(--bad-ink)"><span>Low conversion</span></div>` : nothing}`, e.clientX, e.clientY);
  }

  private draw() {
    const d = this._data;
    if (!d) return;
    const height = d.height ?? 520;
    if (this.error) { render(html`<div class="map-wrap" style="height:${height}px"><div class="empty-state">${this.error}</div></div>`, this); return; }
    if (!this.geo) { render(html`<div class="map-wrap skeleton" style="height:${height}px"></div>`, this); return; }
    if (d.fitKey !== this.lastFit) {
      render(html`<div class="map-wrap" style="height:${height}px"></div>`, this);
      this.fit(d.counties);
      this.lastFit = d.fitKey;
    }
    const byId = new Map(d.counties.map((c) => [c.fips, c]));
    const steps = d.metric === "revenue" ? quantileSteps(d.counties.map((c) => c.ytd)) : [];
    const terrSet = d.territoryRepIds && d.territoryRepIds.length ? new Set(d.counties.filter((c) => c.reps.some((r) => d.territoryRepIds!.includes(r))).map((c) => c.fips)) : null;
    const terrPath = terrSet && terrSet.size
      ? geoPath()(merge(this.geo.topo, (this.geo.topo.objects.counties.geometries as (Polygon | MultiPolygon)[]).filter((g) => terrSet.has(String(g.id))))) ?? ""
      : "";
    const lowIds = d.showLowConversion ? d.counties.filter((c) => c.lowConversion).map((c) => c.fips) : [];
    const low = new Set(lowIds);
    const legend = legendFor(d.metric, steps, !!terrSet);
    const tpl: TemplateResult = html`<div class="map-wrap" style="height:${height}px">
      <svg viewBox="${this.view.x} ${this.view.y} ${this.view.w} ${this.view.h}" preserveAspectRatio="xMidYMid meet"
        @pointerdown=${this.onPointerDown} @pointermove=${this.onPointerMove} @pointerup=${this.onPointerUp} @pointercancel=${this.onPointerUp}
        @pointerleave=${() => hideTip()} @wheel=${{ handleEvent: this.onWheel, passive: false }} role="img" aria-label="County map">
        <defs><pattern id="hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="1.6" height="5" style="fill:var(--bad-ink)" opacity=".9"></rect></pattern></defs>
        <g>${this.geo.paths.map((p) => {
          const c = byId.get(p.id);
          const { cls } = colorFor(d.metric, c, steps, terrSet);
          return svg`<path class="county ${cls || "empty"} ${d.selected === p.id ? "sel" : ""}" d=${p.d} data-fips=${p.id}></path>`;
        })}</g>
        <g>${this.geo.paths.filter((p) => low.has(p.id)).map((p) => svg`<path class="lowconv" d=${p.d}></path>`)}</g>
        <path class="states" d=${this.geo.statesPath}></path>
        ${terrPath ? svg`<path class="terr" d=${terrPath}></path>` : nothing}
      </svg>
      <div class="map-tools">
        <button class="btn glass" title="Zoom in" aria-label="Zoom in" @click=${() => this.zoom(1.5)}>${icons.plus}</button>
        <button class="btn glass" title="Zoom out" aria-label="Zoom out" @click=${() => this.zoom(1 / 1.5)}>${icons.minus}</button>
        <button class="btn glass" title="Fit to data" aria-label="Fit to data" @click=${() => { this.fit(d.counties); this.applyView(); }}>${icons.reset}</button>
      </div>
      <div class="map-legend glass">
        <div class="muted">${legend.note}</div>
        <div class="ramp">${legend.colors.map((c) => html`<span style="background:${c}"></span>`)}</div>
        <div class="ramp-labels"><span>${legend.labels[0]}</span><span>${legend.labels[1]}</span></div>
        ${d.showLowConversion ? html`<div class="row small"><span class="hatch-key"></span> Low quote conversion</div>` : nothing}
        ${terrPath ? html`<div class="row small"><span class="terr-key"></span> Rep territory</div>` : nothing}
      </div>
    </div>`;
    render(tpl, this);
    this.svgEl = this.querySelector("svg");
  }
}

customElements.define("ff-map", CountyMapEl);
